// @ts-nocheck
import { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import LineItemCard from '../LineItemCard/LineItemCard';
import * as PropTypes from 'prop-types';
import './LineItemList.css';

/**
 * LineItemList component manages a list of line items with drag & drop reordering.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Array} props.lineItems - Array of line items to display.
 * @param {number} props.tripDayId - The ID of the trip day (null for library items).
 * @param {number} props.tripId - The ID of the trip.
 * @param {number} props.tenantId - The ID of the tenant.
 * @param {function} props.onEdit - Callback function to edit a line item.
 * @param {function} props.onDelete - Callback function to delete a line item.
 * @param {function} props.onReorder - Callback function when items are reordered.
 * @param {boolean} props.isLibrary - Whether this is for library items.
 * @param {boolean} props.readOnly - Whether the list is read-only.
 *
 * @returns {JSX.Element} A JSX element representing the line item list.
 */
const LineItemList = ({
  lineItems = [],
  tripDayId,
  tripId,
  tenantId,
  onEdit,
  onDelete,
  onReorder,
  isLibrary = false,
  readOnly = false,
  isDropDisabled = false,
  emptyStateActions = null,
  ...rest
}) => {
  const { t } = useTranslation(['trip', 'common']);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const sortedLineItems = [...lineItems].sort(
    (a, b) => (a.seq || 0) - (b.seq || 0)
  );

  const handleEdit = (lineItem) => {
    if (onEdit && !readOnly) {
      onEdit(lineItem);
    }
  };

  const handleDelete = async (lineItemId) => {
    if (onDelete && !readOnly) {
      await onDelete(lineItemId);
    }
  };

  const droppableId = `line-items-${tripDayId || 'library'}`;

  return (
    <Box className='line-item-list' {...rest}>
      {isMounted && (
        <Droppable droppableId={droppableId} isDropDisabled={isDropDisabled}>
          {(provided, snapshot) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`line-item-list__droppable ${
                sortedLineItems.length === 0
                  ? 'line-item-list__droppable--empty'
                  : ''
              } ${
                (snapshot.isDraggingOver && !isDropDisabled) ||
                (snapshot.isDraggingOver && sortedLineItems.length === 0)
                  ? 'line-item-list__droppable--dragging-over'
                  : ''
              }`}
            >
              {sortedLineItems.length === 0 ? (
                <Box className='line-item-list__empty'>
                  <Typography variant='body2' color='textSecondary'>
                    {isLibrary
                      ? t('trip:noLibraryServices')
                      : t('trip:noServicesAddedYet')}
                  </Typography>
                  {emptyStateActions}
                </Box>
              ) : (
                sortedLineItems.map((lineItem, index) => (
                  <Draggable
                    key={lineItem.lineitemid}
                    draggableId={`line-item-${lineItem.lineitemid}`}
                    index={index}
                    isDragDisabled={readOnly || !onReorder}
                  >
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        sx={{
                          opacity: snapshot.isDragging ? 0.8 : 1,
                          transform: snapshot.isDragging
                            ? 'rotate(2deg)'
                            : 'none'
                        }}
                      >
                        <LineItemCard
                          lineItem={lineItem}
                          onEdit={readOnly ? null : handleEdit}
                          onDelete={readOnly ? null : handleDelete}
                          tripId={tripId}
                          tenantId={tenantId}
                          isLibraryItem={isLibrary}
                          isDraggable={!readOnly && onReorder ? true : false}
                          dragHandleProps={provided.dragHandleProps}
                        />
                      </Box>
                    )}
                  </Draggable>
                ))
              )}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      )}
    </Box>
  );
};

LineItemList.propTypes = {
  lineItems: PropTypes.array,
  tripDayId: PropTypes.number,
  tripId: PropTypes.number,
  tenantId: PropTypes.number,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onReorder: PropTypes.func,
  isLibrary: PropTypes.bool,
  readOnly: PropTypes.bool,
  isDropDisabled: PropTypes.bool,
  emptyStateActions: PropTypes.node
};

export default LineItemList;
